import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
} from '@mui/material';
import {
  SearchRounded,
  CheckCircleRounded,
  WorkspacePremiumRounded,
  PeopleAltRounded,
  SchoolRounded,
} from '@mui/icons-material';
import { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useUiStore } from '../../../../stores/ui.store';
import { KpiCard } from './KpiCard';
import { pct } from './shared';
import { StudentCoursesDialog } from './StudentCoursesDialog';
import { useTenantConfig } from '../../../../hooks/use-tenant-config';
import { type AcademicRow, type ReportKpis } from '../../../../hooks/use-reports';

// ─── Academic Tab ─────────────────────────────────────────────────────────────

/**
 * Window-virtualizes a long table body via spacer rows. Keeps the real
 * `<Table>` (and its responsive `<TableCell>`s) intact — only the on-screen
 * window of rows is mounted; a top + bottom spacer `<TableRow>` reserves the
 * scroll height for the rest. Scrolls against the window, so the page keeps its
 * natural scroll. `containerRef` goes on the `<TableContainer>` (its `offsetTop`
 * is the table's document position).
 */
export function useTableWindowVirtualizer(count: number) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  useLayoutEffect(() => {
    const top = containerRef.current?.offsetTop ?? 0;
    setOffset((prev) => (prev === top ? prev : top));
  }, [count]);
  const virtualizer = useWindowVirtualizer({
    count,
    estimateSize: () => 53,
    overscan: 10,
    scrollMargin: offset,
  });
  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = items.length > 0 ? items[0]!.start - offset : 0;
  const paddingBottom = items.length > 0 ? totalSize - items[items.length - 1]!.end + offset : 0;
  return { containerRef, virtualizer, items, paddingTop, paddingBottom };
}

export function AcademicTab({
  rows,
  filtered,
  search,
  setSearch,
  enrollFilter,
  setEnrollFilter,
  planFilter,
  setPlanFilter,
  programFilter,
  setProgramFilter,
  kpis,
}: {
  kpis: ReportKpis;
  rows: AcademicRow[];
  filtered: AcademicRow[];
  search: string;
  setSearch: (s: string) => void;
  enrollFilter: 'all' | 'enrolled' | 'not_enrolled';
  setEnrollFilter: (s: 'all' | 'enrolled' | 'not_enrolled') => void;
  planFilter: string;
  setPlanFilter: (s: string) => void;
  programFilter: string;
  setProgramFilter: (s: string) => void;
}) {
  const { colorMode } = useUiStore();
  const isDark = colorMode === 'dark';
  const [selectedStudent, setSelectedStudent] = useState<AcademicRow | null>(null);
  const importedRecord = useTenantConfig().academicRecord.imported;
  const { containerRef, virtualizer, items, paddingTop, paddingBottom } = useTableWindowVirtualizer(
    filtered.length,
  );

  // Collect unique plan types and programs for filter dropdowns
  const planTypeOptions = useMemo(() => {
    const types = new Set(rows.map((r) => r.planType).filter((t) => t && t !== '—'));
    return Array.from(types).sort();
  }, [rows]);
  const programOptions = useMemo(() => {
    const progs = new Set(rows.map((r) => r.programName).filter((p) => p && p !== '—'));
    return Array.from(progs).sort();
  }, [rows]);

  return (
    <>
      {/* Los indicadores viven DENTRO de su pestaña. En la cabecera afirmaban
          ser relevantes para las seis: quien miraba Cobertura veía "Monto
          vencido", que no tiene nada que ver con lo que estaba haciendo. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <KpiCard
          isDark={isDark}
          icon={<PeopleAltRounded fontSize="inherit" />}
          label="Personas activas"
          value={kpis.totalActivePeople.toString()}
        />
        <KpiCard
          isDark={isDark}
          icon={<SchoolRounded fontSize="inherit" />}
          label="Inscritos"
          value={kpis.enrolledStudents.toString()}
          sub={`${pct(kpis.enrollmentRate)} del total`}
          variant="success"
        />
      </Box>

      {/* Filters */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        mb={2}
        alignItems={{ sm: 'center' }}
      >
        <TextField
          size="small"
          placeholder="Buscar persona o programa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select value={programFilter} onChange={(e) => setProgramFilter(e.target.value)}>
            <MenuItem value="all">Todos los programas</MenuItem>
            {programOptions.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={enrollFilter}
            onChange={(e) => setEnrollFilter(e.target.value as typeof enrollFilter)}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="enrolled">Con ramos inscritos</MenuItem>
            <MenuItem value="not_enrolled">Sin ramos inscritos</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
            <MenuItem value="all">Todos los planes</MenuItem>
            {planTypeOptions.map((pt) => (
              <MenuItem key={pt} value={pt}>
                {pt}
              </MenuItem>
            ))}
            <MenuItem value="__exempt__">Exento</MenuItem>
            <MenuItem value="__no_plan__">Sin plan</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {filtered.length} persona{filtered.length !== 1 ? 's' : ''}
        </Typography>
      </Stack>

      <TableContainer
        ref={containerRef}
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, overflowX: 'auto' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600, fontSize: 12 } }}>
              <TableCell>Nombre</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Programa</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Tipo</TableCell>
              <TableCell>Ramos inscritos</TableCell>
              <TableCell>Plan de pago</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Correo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No se encontraron personas con los filtros actuales
                </TableCell>
              </TableRow>
            ) : (
              <>
                {paddingTop > 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, border: 0, height: paddingTop }} />
                  </TableRow>
                )}
                {items.map((vItem) => {
                  const row = filtered[vItem.index];
                  if (!row) return null;
                  return (
                    <TableRow
                      key={row.personId}
                      data-index={vItem.index}
                      ref={virtualizer.measureElement}
                      hover
                      onClick={() => setSelectedStudent(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography fontWeight={500} fontSize={13}>
                          {row.name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography
                          variant="body2"
                          color={row.programName !== '—' ? 'text.primary' : 'text.disabled'}
                          fontWeight={row.programName !== '—' ? 500 : 400}
                        >
                          {row.programName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {row.personType || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            row.enrolledSections > 0
                              ? `${row.enrolledSections} ramo${row.enrolledSections !== 1 ? 's' : ''}`
                              : 'Sin inscripción'
                          }
                          color={row.enrolledSections > 0 ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {row.isExempt ? (
                          <Chip
                            size="small"
                            icon={<WorkspacePremiumRounded />}
                            label="Exento"
                            color="warning"
                            variant="filled"
                            sx={{ fontSize: 11 }}
                          />
                        ) : row.hasActivePlan ? (
                          <Chip
                            size="small"
                            icon={<CheckCircleRounded />}
                            label="Activo"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip size="small" label="Sin plan" color="default" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {row.email || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {paddingBottom > 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, border: 0, height: paddingBottom }} />
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <StudentCoursesDialog
        row={selectedStudent}
        onClose={() => setSelectedStudent(null)}
        isDark={isDark}
        hideGrades={importedRecord}
      />
    </>
  );
}
